import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Badge,
  TableCell, TableRow
} from '@one-thd/sui-atomic-components';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import {
  extend,
  params,
  shape as shapeType,
  string as stringType
} from '@thd-nucleus/data-sources';
import { Price } from '@thd-olt-component-react/price';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { ProductImage } from '@thd-olt-component-react/product-pod';
import { QuickEstimateItemPlaceholder } from './QuickEstimateItemPlaceholder';
import { SparkleIcon } from "./SparkleIcon";


export const QuickEstimateItem = ({
  item,
  itemIndex
}) => {
  const { itemId, intent, quantity = 1, isAutoResolved = false } = item;
  const { storeId } = useStore();
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  // Check if this intent is still being resolved (no resolved product yet for an intent item)
  const isLoading = intent && !itemId;


  if (!item?.itemId && !item.intent) {
    return null;
  }
  if (item?.resolveFailed) {
    return (
      <TableRow data-testid={`quick-estimate-item-${itemIndex}`}>
        <TableCell colSpan="3" className="sui-bg-primary sui-text-left sui-px-1 sui-py-2">
          <Alert status="warning">
            Unable to find a recommended product for "<b>{item.intent}</b>"
          </Alert>
        </TableCell>
      </TableRow>
    )
  }
  if (isLoading) {
    return <QuickEstimateItemPlaceholder itemIndex={itemIndex} />;
  }

  return (
    <TableRow data-testid={`quick-estimate-item-${itemIndex}`}>
      <TableCell className="sui-bg-primary sui-text-left sui-px-1 sui-py-2">
        <div className="sui-flex sui-gap-2 sui-pointer-events-none">
          <div className="sui-w-12 sui-min-w-12 sui-max-h-20 sui-overflow-hidden sui-align-middle">
            <ProductImage itemId={itemId} />
          </div>
          <div>
            {
              isAutoResolved && (
                <Badge startIcon={<SparkleIcon />} variant="strong" color="brand">
                  Recommended
                </Badge>
              )
            }
            <ProductHeader
              itemId={itemId}
              storeId={storeId}
              brand="inline"
              brandFontSize="sm"
              titleMaxLine={isMobile ? 3 : 2}
              disableShopThisCollection
            />
          </div>
        </div>
      </TableCell>
      <TableCell className="sui-bg-primary sui-text-center sui-px-2 sui-py-2">
        {quantity}
      </TableCell>
      <TableCell className="sui-bg-primary sui-text-center sui-px-2 sui-py-2">
        <Price
          itemId={itemId}
          storeId={storeId}
          large={false}
          type="simple"
          hideBadge
          hideWhenZero
          hideBulkPrice
          hideLimitPerOrder
          hideSavingsText
          stackWasPrice
          omitDetails
        />
      </TableCell>
    </TableRow>
  );
};

QuickEstimateItem.displayName = 'QuickEstimateItem';

QuickEstimateItem.propTypes = {
  item: PropTypes.shape({
    itemId: PropTypes.string,
    intentId: PropTypes.string,
    quantity: PropTypes.number,
    intent: PropTypes.string
  }).isRequired,
  itemIndex: PropTypes.number
};

QuickEstimateItem.defaultProps = {
  itemIndex: 0
};

QuickEstimateItem.dataModel = extend({
  product: params({
    itemId: stringType().isRequired(), dataSource: stringType()
  }).shape({
    dataSources: stringType(),
    identifiers: shapeType({
      productLabel: stringType().isRequired(),
      storeSkuNumber: stringType(),
      omsThdSku: stringType(),
      specialOrderSku: stringType()
    })
  })
},
ProductHeader,
ProductImage,
Price
);
